import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "../components/ui/dialog";
import { Plus, Copy, Download, LogOut, TicketPlus, RefreshCw, Check, X, Trash2, RotateCcw, FileText } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/clarity-zones-logo.png";

const STATUS_COLORS = {
  aktiv: "bg-emerald-100 text-emerald-800",
  "eingelöst": "bg-blue-100 text-blue-800",
  deaktiviert: "bg-slate-100 text-slate-600",
  abgelaufen: "bg-amber-100 text-amber-800",
};

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white border border-slate-100 rounded-lg p-5">
      <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
      <p className={`font-mono text-3xl font-bold ${color || "text-slate-900"}`}>{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { adminToken, logoutAdmin } = useAuth();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [events, setEvents] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterEvent, setFilterEvent] = useState("");
  const [copied, setCopied] = useState(false);

  // Create single state
  const [singleNote, setSingleNote] = useState("");
  const [singleExpires, setSingleExpires] = useState("");
  const [singleEvent, setSingleEvent] = useState("");
  const [createdVoucher, setCreatedVoucher] = useState(null);

  // Batch state
  const [batchCount, setBatchCount] = useState(10);
  const [batchEventName, setBatchEventName] = useState("");
  const [batchExpires, setBatchExpires] = useState("");
  const [batchNote, setBatchNote] = useState("");
  const [createdBatch, setCreatedBatch] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [reportView, setReportView] = useState(null);
  const [reportAliasMap, setReportAliasMap] = useState({});

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${adminToken}` }), [adminToken]);

  const fetchAll = useCallback(async () => {
    const h = getHeaders();
    const bust = `_t=${Date.now()}`; // cache-busting
    try {
      const [dashRes, vouchRes, evtRes, repRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard?${bust}`, { headers: h }),
        axios.get(`${API}/admin/vouchers`, { headers: h, params: { status: filterStatus || undefined, event_id: filterEvent || undefined, _t: Date.now() } }),
        axios.get(`${API}/admin/events?${bust}`, { headers: h }),
        axios.get(`${API}/admin/reports?${bust}`, { headers: h }),
      ]);
      setDashboard(dashRes.data);
      setVouchers(vouchRes.data);
      setEvents(evtRes.data);
      const map = {};
      repRes.data.forEach((r) => { map[r.voucher_id] = r.alias; });
      setReportAliasMap(map);
    } catch (err) {
      if (err.response?.status === 401) {
        logoutAdmin();
        navigate("/admin");
      }
    }
  }, [adminToken, filterStatus, filterEvent, getHeaders, logoutAdmin, navigate]);

  // Initial load + filter change
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(fetchAll, 15000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const createSingle = async () => {
    try {
      const res = await axios.post(`${API}/admin/vouchers`, {
        note: singleNote || null,
        expires_at: singleExpires || null,
        event_id: singleEvent || null,
        event_name: singleEvent ? events.find(e => e.id === singleEvent)?.name : null,
      }, { headers: getHeaders() });
      setCreatedVoucher(res.data);
      setSingleNote("");
      setSingleExpires("");
      await fetchAll();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
    }
  };

  const createBatch = async () => {
    if (!batchEventName.trim() || batchCount < 1) return;
    setBatchLoading(true);
    try {
      const res = await axios.post(`${API}/admin/vouchers/batch`, {
        count: batchCount,
        event_name: batchEventName.trim(),
        expires_at: batchExpires || null,
        note: batchNote || null,
      }, { headers: getHeaders() });
      setCreatedBatch(res.data);
      setBatchEventName("");
      setBatchNote("");
      await fetchAll();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
    } finally {
      setBatchLoading(false);
    }
  };

  const toggleStatus = async (voucher) => {
    const newStatus = voucher.status === "aktiv" ? "deaktiviert" : "aktiv";
    await axios.patch(`${API}/admin/vouchers/${voucher.id}`, { status: newStatus }, { headers: getHeaders() });
    await fetchAll();
  };

  const deleteVoucher = async (id) => {
    await axios.delete(`${API}/admin/vouchers/${id}`, { headers: getHeaders() });
    setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    await fetchAll();
  };

  const reactivateVoucher = async (id) => {
    await axios.post(`${API}/admin/vouchers/batch-reactivate`, [id], { headers: getHeaders() });
    await fetchAll();
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    await axios.post(`${API}/admin/vouchers/batch-delete`, [...selected], { headers: getHeaders() });
    setSelected(new Set());
    await fetchAll();
  };

  const bulkReactivate = async () => {
    if (selected.size === 0) return;
    await axios.post(`${API}/admin/vouchers/batch-reactivate`, [...selected], { headers: getHeaders() });
    setSelected(new Set());
    await fetchAll();
  };

  const viewReport = async (voucherId) => {
    try {
      const res = await axios.get(`${API}/admin/reports/${voucherId}`, { headers: getHeaders() });
      setReportView(res.data);
    } catch (err) {
      setReportView(null);
    }
  };

  const deleteReport = async (voucherId) => {
    await axios.delete(`${API}/admin/reports/${voucherId}`, { headers: getHeaders() });
    setReportView(null);
    await fetchAll();
  };

  const downloadReportJson = (data) => {
    const json = JSON.stringify(data.report_data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-${data.alias || "unknown"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === vouchers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(vouchers.map((v) => v.id)));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const exportVouchers = (list) => {
    const header = "Alias\tPasswort\tStatus\tEvent\tErstellt";
    const rows = list.map(v => `${v.alias}\t${v.password}\t${v.status}\t${v.event_name || "-"}\t${v.created_at?.slice(0, 10)}`);
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "zugaenge.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatCredentials = (list) =>
    list.map(v => `${v.alias} / ${v.password}`).join("\n");

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Clarity Zones" className="h-8 w-auto" />
            <Badge variant="outline" className="text-xs font-mono">ADMIN</Badge>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={fetchAll} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logoutAdmin(); navigate("/admin"); }} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="vouchers" data-testid="tab-vouchers">Zugänge</TabsTrigger>
          </TabsList>

          {/* ═══ DASHBOARD TAB ═══ */}
          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Aktiv" value={dashboard.aktiv} color="text-emerald-600" />
              <StatCard label="Eingelöst" value={dashboard.eingeloest} color="text-blue-600" />
              <StatCard label="Deaktiviert" value={dashboard.deaktiviert} color="text-slate-500" />
              <StatCard label="Abgelaufen" value={dashboard.abgelaufen} color="text-amber-600" />
            </div>

            {dashboard.event_stats.length > 0 && (
              <div className="bg-white border border-slate-100 rounded-lg p-6">
                <h3 className="font-bold text-sm text-slate-900 mb-4">Events / Kontingente</h3>
                <div className="space-y-3">
                  {dashboard.event_stats.map((ev) => (
                    <div key={ev.event_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm text-slate-900">{ev.event_name}</p>
                        <p className="text-xs text-slate-500">{ev.total} Zugänge</p>
                      </div>
                      <div className="flex gap-3 text-xs">
                        <span className="text-emerald-600 font-mono">{ev.aktiv} aktiv</span>
                        <span className="text-blue-600 font-mono">{ev.eingeloest} eingelöst</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ═══ VOUCHERS TAB ═══ */}
          <TabsContent value="vouchers" className="space-y-4">
            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Create single */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800 gap-1.5" data-testid="create-single-btn">
                    <Plus className="w-4 h-4" /> Zugang erstellen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Einzelnen Zugang erstellen</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-1">
                      <label className="text-sm text-slate-600">Notiz (optional)</label>
                      <Input value={singleNote} onChange={(e) => setSingleNote(e.target.value)} placeholder="z.B. VIP-Zugang" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm text-slate-600">Ablaufdatum (optional)</label>
                      <Input type="date" value={singleExpires} onChange={(e) => setSingleExpires(e.target.value)} />
                    </div>
                    {events.length > 0 && (
                      <div className="space-y-1">
                        <label className="text-sm text-slate-600">Event zuordnen (optional)</label>
                        <select
                          value={singleEvent}
                          onChange={(e) => setSingleEvent(e.target.value)}
                          className="w-full h-10 border border-slate-200 rounded-sm px-3 text-sm"
                        >
                          <option value="">Kein Event</option>
                          {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                  {createdVoucher ? (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
                      <p className="text-sm font-bold text-emerald-800">Zugang erstellt!</p>
                      <div className="font-mono text-sm bg-white p-3 rounded border">
                        <p>Alias: <strong>{createdVoucher.alias}</strong></p>
                        <p>Passwort: <strong>{createdVoucher.password}</strong></p>
                      </div>
                      <Button size="sm" variant="outline" className="gap-1.5 relative z-50" onClick={() => copyToClipboard(`${createdVoucher.alias} / ${createdVoucher.password}`)}>
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Kopieren
                      </Button>
                    </div>
                  ) : (
                    <DialogFooter>
                      <Button onClick={createSingle} className="bg-slate-900 text-white hover:bg-slate-800 relative z-50">Erstellen</Button>
                    </DialogFooter>
                  )}
                  {createdVoucher && (
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline" onClick={() => setCreatedVoucher(null)}>Schließen</Button>
                      </DialogClose>
                    </DialogFooter>
                  )}
                </DialogContent>
              </Dialog>

              {/* Create batch */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-1.5" data-testid="create-batch-btn">
                    <TicketPlus className="w-4 h-4" /> Kontingent erstellen
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Event-Zugänge erzeugen</DialogTitle></DialogHeader>
                  {!createdBatch ? (
                    <div className="space-y-4 py-2">
                      <div className="space-y-1">
                        <label className="text-sm text-slate-600 font-medium">Event-Name *</label>
                        <Input value={batchEventName} onChange={(e) => setBatchEventName(e.target.value)} placeholder="z.B. Humanskills Event Mai 2026" data-testid="batch-event-name" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm text-slate-600">Anzahl Zugänge</label>
                        <Input type="number" min={1} max={500} value={batchCount} onChange={(e) => setBatchCount(parseInt(e.target.value) || 1)} data-testid="batch-count" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm text-slate-600">Ablaufdatum (optional)</label>
                        <Input type="date" value={batchExpires} onChange={(e) => setBatchExpires(e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm text-slate-600">Notiz (optional)</label>
                        <Input value={batchNote} onChange={(e) => setBatchNote(e.target.value)} placeholder="Interne Notiz" />
                      </div>
                      <DialogFooter>
                        <Button onClick={createBatch} disabled={batchLoading || !batchEventName.trim()} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="batch-create-submit">
                          {batchLoading ? "Wird erstellt..." : `${batchCount} Zugänge erstellen`}
                        </Button>
                      </DialogFooter>
                    </div>
                  ) : (
                    <div className="space-y-4 py-2">
                      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <p className="text-sm font-bold text-emerald-800 mb-1">
                          {createdBatch.vouchers.length} Zugänge für &quot;{createdBatch.event.name}&quot; erstellt!
                        </p>
                      </div>
                      <div className="max-h-60 overflow-y-auto border rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-slate-600">Alias</th>
                              <th className="text-left px-3 py-2 font-medium text-slate-600">Passwort</th>
                            </tr>
                          </thead>
                          <tbody>
                            {createdBatch.vouchers.map((v) => (
                              <tr key={v.id} className="border-t">
                                <td className="px-3 py-2 font-mono text-xs">{v.alias}</td>
                                <td className="px-3 py-2 font-mono text-xs">{v.password}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => copyToClipboard(formatCredentials(createdBatch.vouchers))}>
                          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Alle kopieren
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => exportVouchers(createdBatch.vouchers)}>
                          <Download className="w-3 h-3" /> CSV exportieren
                        </Button>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline" onClick={() => setCreatedBatch(null)}>Schließen</Button>
                        </DialogClose>
                      </DialogFooter>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* Export all */}
              <Button size="sm" variant="ghost" className="gap-1.5 text-slate-500" onClick={() => exportVouchers(vouchers)} data-testid="export-all-btn">
                <Download className="w-4 h-4" /> Exportieren
              </Button>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Filters */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="h-9 border border-slate-200 rounded-sm px-3 text-sm bg-white"
                data-testid="filter-status"
              >
                <option value="">Alle Status</option>
                <option value="aktiv">Aktiv</option>
                <option value="eingelöst">Eingelöst</option>
                <option value="deaktiviert">Deaktiviert</option>
                <option value="abgelaufen">Abgelaufen</option>
              </select>

              <select
                value={filterEvent}
                onChange={(e) => setFilterEvent(e.target.value)}
                className="h-9 border border-slate-200 rounded-sm px-3 text-sm bg-white"
                data-testid="filter-event"
              >
                <option value="">Alle Events</option>
                {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
              </select>
            </div>

            {/* Bulk action bar */}
            {selected.size > 0 && (
              <div className="flex items-center gap-3 p-3 bg-slate-900 text-white rounded-lg" data-testid="bulk-actions">
                <span className="text-sm font-medium">{selected.size} ausgewählt</span>
                <div className="flex-1" />
                <Button size="sm" variant="ghost" className="text-emerald-300 hover:text-emerald-100 hover:bg-emerald-900/30 gap-1.5" onClick={bulkReactivate} data-testid="bulk-reactivate-btn">
                  <RotateCcw className="w-3.5 h-3.5" /> Reaktivieren
                </Button>
                <Button size="sm" variant="ghost" className="text-red-300 hover:text-red-100 hover:bg-red-900/30 gap-1.5" onClick={bulkDelete} data-testid="bulk-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Löschen
                </Button>
                <button onClick={() => setSelected(new Set())} className="text-slate-400 hover:text-white ml-2">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Voucher table */}
            <div className="bg-white border border-slate-100 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="voucher-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={vouchers.length > 0 && selected.size === vouchers.length}
                          onChange={toggleSelectAll}
                          className="rounded border-slate-300"
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Alias</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Passwort</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Event</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Erstellt</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">Ablauf</th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-700">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vouchers.map((v) => (
                      <tr key={v.id} className={`border-t border-slate-50 ${selected.has(v.id) ? "bg-slate-50" : "hover:bg-slate-50/50"}`}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selected.has(v.id)}
                            onChange={() => toggleSelect(v.id)}
                            className="rounded border-slate-300"
                          />
                        </td>
                        <td className="px-4 py-3 font-mono text-xs font-medium">{v.alias}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{v.password}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[v.status] || "bg-slate-100"}`}>
                            {v.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{v.event_name || "—"}</td>
                        <td className="px-4 py-3 text-xs text-slate-400">{v.created_at?.slice(0, 10)}</td>
                        <td className="px-4 py-3 text-xs text-slate-400">{v.expires_at?.slice(0, 10) || "—"}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => copyToClipboard(`${v.alias} / ${v.password}`)}
                              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
                              title="Kopieren"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                            {reportAliasMap[v.id] && (
                              <button
                                onClick={() => viewReport(v.id)}
                                className="p-1.5 text-sky-400 hover:text-sky-600 hover:bg-sky-50 rounded"
                                title="Report anzeigen"
                                data-testid={`view-report-${v.id}`}
                              >
                                <FileText className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {v.status !== "aktiv" && (
                              <button
                                onClick={() => reactivateVoucher(v.id)}
                                className="p-1.5 text-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                                title="Reaktivieren"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {v.status === "aktiv" && (
                              <button
                                onClick={() => toggleStatus(v)}
                                className="p-1.5 text-amber-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                                title="Deaktivieren"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteVoucher(v.id)}
                              className="p-1.5 text-red-300 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Löschen"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {vouchers.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-400">
                          Keine Zugänge gefunden
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-slate-100 px-4 py-3 bg-slate-50">
                <p className="text-xs text-slate-400">{vouchers.length} Zugänge</p>
              </div>
            </div>

            {/* Report viewer dialog */}
            {reportView && (
              <Dialog open={!!reportView} onOpenChange={(open) => { if (!open) setReportView(null); }}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Report: {reportView.alias}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="text-xs text-slate-400">
                      Gespeichert: {reportView.created_at ? new Date(reportView.created_at).toLocaleString("de-DE") : "—"} · Wird nach 7 Tagen automatisch gelöscht
                    </div>

                    {/* Zones */}
                    {reportView.report_data?.zones && (
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-xs font-semibold text-slate-600 mb-2">Zonen-Scores</p>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(reportView.report_data.zones).map(([name, z]) => (
                            <div key={name} className="flex justify-between text-sm">
                              <span className="text-slate-700">{name}</span>
                              <span className="font-mono text-slate-900">{z.score} ({z.status})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Ranking */}
                    {reportView.report_data?.ranking && (
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-xs font-semibold text-slate-600 mb-2">Ranking</p>
                        <p className="text-sm text-slate-700">
                          1. {reportView.report_data.ranking.primary_zone} ({reportView.report_data.ranking.primary_score}) · 2. {reportView.report_data.ranking.secondary_zone} ({reportView.report_data.ranking.secondary_score})
                        </p>
                      </div>
                    )}

                    {/* Pathos/Logos */}
                    {reportView.report_data?.pathos_logos && (
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-xs font-semibold text-slate-600 mb-2">Wirkungsstil</p>
                        <p className="text-sm text-slate-700">
                          Pathos: {reportView.report_data.pathos_logos.pathos} · Logos: {reportView.report_data.pathos_logos.logos} · Typ: {reportView.report_data.pathos_logos.effect_type}
                        </p>
                      </div>
                    )}

                    {/* Facets */}
                    {reportView.report_data?.facets && (
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-xs font-semibold text-slate-600 mb-2">Facetten</p>
                        <div className="grid grid-cols-2 gap-1">
                          {Object.entries(reportView.report_data.facets).map(([name, f]) => (
                            <div key={name} className="flex justify-between text-xs">
                              <span className="text-slate-600">{name}</span>
                              <span className="font-mono text-slate-800">{f.score} ({f.level})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter className="gap-2">
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => downloadReportJson(reportView)}>
                      <Download className="w-3 h-3" /> JSON herunterladen
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteReport(reportView.voucher_id)}>
                      <Trash2 className="w-3 h-3" /> Report löschen
                    </Button>
                    <DialogClose asChild>
                      <Button variant="outline" size="sm">Schließen</Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
